import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt, faFileAlt, faMoneyBillWave, faCoins,
  faPercent, faHandHoldingUsd, faChartLine, faChartPie,
  faFileExport, faDroplet,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../components/shared/DataTable';
import { fmtMoney } from '../utils/formatMoney';
import { UiPageHeader, UiButton, UiSelect, UiStatsGrid } from '../components/ui';
import type { StatCardDef } from '../components/ui';
import P2pReportPanel, { P2pReportSummary } from '../components/P2pReportPanel';

const MerchantMonthlyReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const months = t('monthlyReports.months', { returnObjects: true }) as string[];
  const days = t('monthlyReports.days', { returnObjects: true }) as string[];

  const now = new Date(new Date().getTime() + 7 * 60 * 60 * 1000);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [report, setReport] = useState<any>({ days: [], summary: {} });
  const [p2p, setP2p] = useState<P2pReportSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await merchantApi.get(`/merchants/reports/monthly?year=${year}&month=${month}`);
      setReport(response.data.data);
      // 💧 P2P เดือนนี้ (แยก)
      const from = new Date(year, month - 1, 1).toISOString();
      const to = new Date(year, month, 0, 23, 59, 59).toISOString();
      const p2pRes = await merchantApi.get('/merchants/p2p-report', { params: { date_from: from, date_to: to } }).catch(() => null);
      setP2p(p2pRes?.data?.success ? (p2pRes.data.data.summary ?? null) : null);
    } catch (error: any) {
      toast.error(t('monthlyReports.cannotLoad') + ': ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const formatCurrency = (val: number) =>
    `฿${parseFloat(String(val || 0)).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`;

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    const dayName = days[d.getDay()];
    return `${dayName}. ${day}`;
  };

  const yearOptions = Array.from({ length: 3 }, (_, i) => now.getFullYear() - i);

  const handleExportCSV = () => {
    const days = (report.days || []) as any[];
    if (!days.length) {
      toast.warn(t('monthlyReports.noDataToExport'));
      return;
    }

    const escape = (val: any) => {
      const s = val === null || val === undefined ? '' : String(val);
      if (/[",\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const num = (v: any) => Number(v || 0);

    const headers = [
      'Date',
      'Total Orders',
      'Paid Orders',
      'Pending Orders',
      'Expired Orders',
      'Base Revenue (THB)',
      'Total Revenue (THB)',
      'Cent Difference (THB)',
      'Fee Payin (THB)',
      'Payout Count',
      'Payout Amount (THB)',
      'Payout Fee (THB)',
      'Net Revenue (THB)',
      'Success Rate (%)',
    ];

    const rows = days.map((d) => [
      d.date,
      num(d.total_orders),
      num(d.paid_orders),
      num(d.pending_orders),
      num(d.expired_orders),
      num(d.base_revenue).toFixed(2),
      num(d.total_revenue).toFixed(2),
      num(d.cent_difference).toFixed(2),
      num(d.total_fee).toFixed(2),
      num(d.payout_count),
      num(d.payout_amount).toFixed(2),
      num(d.payout_fee).toFixed(2),
      num(d.net_revenue).toFixed(2),
      num(d.success_rate).toFixed(2),
    ]);

    const s = report.summary || {};
    const summaryRow = [
      'TOTAL',
      num(s.total_orders),
      num(s.paid_orders),
      num(s.pending_orders),
      num(s.expired_orders),
      num(s.base_revenue).toFixed(2),
      num(s.total_revenue).toFixed(2),
      num(s.cent_difference).toFixed(2),
      num(s.total_fee).toFixed(2),
      num(s.total_payouts),
      num(s.total_payout_amount).toFixed(2),
      num(s.total_payout_fee).toFixed(2),
      num(s.net_revenue).toFixed(2),
      num(s.success_rate).toFixed(2),
    ];

    const csv = [headers, ...rows, summaryRow]
      .map((r) => r.map(escape).join(','))
      .join('\r\n');

    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const mm = String(month).padStart(2, '0');
    a.href = url;
    a.download = `monthly-report_${year}-${mm}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(t('monthlyReports.exportSuccess'));
  };

  const columns: Column<any>[] = [
    {
      key: 'date',
      header: t('monthlyReports.colDate'),
      render: (d) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs font-bold">
            {new Date(d.date).getDate()}
          </div>
          <div>
            <p className="font-medium text-gray-800 dark:text-slate-100 text-sm">{formatDate(d.date)}</p>
            <p className="text-xs text-gray-400 dark:text-slate-500">{d.date}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'total_orders',
      header: 'Orders',
      sortable: true,
      render: (d) => <span className="font-bold text-gray-800 dark:text-slate-100">{d.total_orders}</span>,
    },
    {
      key: 'paid_orders',
      header: 'Paid',
      sortable: true,
      render: (d) => (
        <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-semibold">
          {d.paid_orders}
        </span>
      ),
    },
    {
      key: 'pending_orders',
      header: 'Pending',
      sortable: true,
      render: (d) => (
        <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded text-xs font-semibold">
          {d.pending_orders}
        </span>
      ),
    },
    {
      key: 'expired_orders',
      header: 'Expired',
      sortable: true,
      render: (d) => (
        <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded text-xs font-semibold">
          {d.expired_orders}
        </span>
      ),
    },
    {
      key: 'base_revenue',
      header: t('monthlyReports.colBaseRevenue'),
      sortable: true,
      render: (d) => (
        <span className="font-semibold" style={{ color: 'var(--theme-accent)' }}>
          {formatCurrency(d.base_revenue)}
        </span>
      ),
    },
    {
      key: 'total_revenue',
      header: t('monthlyReports.colTotalRevenue'),
      sortable: true,
      render: (d) => (
        <span className="font-semibold text-blue-600 dark:text-blue-400">
          {formatCurrency(d.total_revenue)}
        </span>
      ),
    },
    {
      key: 'cent_difference',
      header: t('monthlyReports.colCentDiff'),
      sortable: true,
      render: (d) => (
        <span className="font-semibold text-amber-600 dark:text-amber-400">
          {(d.cent_difference || 0) > 0 ? '+' : ''}฿{fmtMoney(d.cent_difference || 0)}
        </span>
      ),
    },
    {
      key: 'total_fee',
      header: 'Fee (Payin)',
      sortable: true,
      render: (d) => (
        <span className="font-semibold text-red-500 dark:text-red-400">
          -฿{fmtMoney(d.total_fee || 0)}
        </span>
      ),
    },
    {
      key: 'payout_count',
      header: 'Payout',
      sortable: true,
      render: (d) => (
        <div>
          {(d.payout_count || 0) > 0 ? (
            <>
              <span className="font-semibold text-indigo-600 dark:text-indigo-400">{d.payout_count} {t('monthlyReports.items')}</span>
              <div className="text-[11px] text-gray-400 dark:text-slate-500">฿{fmtMoney(d.payout_amount || 0)}</div>
              <div className="text-[11px] text-orange-500 dark:text-orange-400">Fee: ฿{fmtMoney(d.payout_fee || 0)}</div>
            </>
          ) : (
            <span className="text-gray-300 dark:text-slate-600">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'net_revenue',
      header: 'ยอดสุทธิ',
      sortable: true,
      render: (d) => (
        <span className="font-bold text-green-600 dark:text-green-400 text-base">
          {formatCurrency(d.net_revenue)}
        </span>
      ),
    },
    {
      key: 'success_rate',
      header: 'Success %',
      sortable: true,
      render: (d) => (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${d.success_rate}%`,
                background: d.success_rate >= 80 ? 'linear-gradient(to right, #10b981, #34d399)' :
                  d.success_rate >= 50 ? 'linear-gradient(to right, #f59e0b, #fbbf24)' :
                    'linear-gradient(to right, #ef4444, #f87171)',
              }}
            ></div>
          </div>
          <span className={`text-xs font-bold min-w-[40px] text-right
            ${d.success_rate >= 80 ? 'text-green-600 dark:text-green-400' : d.success_rate >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-500 dark:text-red-400'}`}>
            {parseFloat(d.success_rate).toFixed(1)}%
          </span>
        </div>
      ),
    },
  ];

  const summary = report.summary || {};
  const mutedSub = (text: string) => <span className="text-xs text-gray-400 dark:text-slate-500">{text}</span>;

  const summaryCards: StatCardDef[] = [
    {
      icon: faFileAlt, iconClassName: 'text-white', iconStyle: { background: 'var(--theme-accent)' },
      label: 'Total Orders',
      value: <span style={{ color: 'var(--theme-accent)' }}>{(summary.total_orders || 0).toLocaleString()}</span>,
      subValue: mutedSub(t('monthlyReports.wholeMonth')),
    },
    {
      icon: faMoneyBillWave, iconClassName: 'text-white', iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: t('monthlyReports.depositBase'),
      value: <span style={{ color: 'var(--theme-primary)' }}>{formatCurrency(summary.base_revenue)}</span>,
      subValue: mutedSub(t('monthlyReports.customerAmount')),
    },
    {
      icon: faCoins, iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: t('monthlyReports.actualRevenue'),
      value: <span className="text-blue-600 dark:text-blue-400">{formatCurrency(summary.total_revenue)}</span>,
      subValue: mutedSub(t('monthlyReports.includeCents')),
    },
    {
      icon: faCoins, iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: t('monthlyReports.centDiff'),
      value: <span className="text-amber-600 dark:text-amber-400">{(summary.cent_difference || 0) > 0 ? '+' : ''}฿{fmtMoney(summary.cent_difference || 0)}</span>,
      subValue: mutedSub(t('monthlyReports.randomCents')),
    },
    {
      icon: faPercent, iconClassName: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: t('monthlyReports.fee'),
      value: <span className="text-red-600 dark:text-red-400">-฿{fmtMoney(summary.total_fee || 0)}</span>,
      subValue: mutedSub(t('monthlyReports.totalFee')),
    },
    {
      icon: faHandHoldingUsd, iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: t('monthlyReports.netRevenue'),
      value: <span className="text-green-600 dark:text-green-400">{formatCurrency(summary.net_revenue)}</span>,
      subValue: mutedSub(t('monthlyReports.afterFee')),
    },
    {
      icon: faChartLine, iconClassName: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
      label: 'Success Rate',
      value: <span className="text-emerald-600 dark:text-emerald-400">{parseFloat(summary.success_rate || 0).toFixed(1)}%</span>,
      subValue: mutedSub(t('monthlyReports.successRate')),
    },
  ];

  const payoutCards: StatCardDef[] = [
    {
      icon: faMoneyBillWave, iconClassName: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
      label: t('monthlyReports.monthlyPayout'),
      value: <span className="text-indigo-600 dark:text-indigo-400">{(summary.total_payouts || 0).toLocaleString()} {t('monthlyReports.items')}</span>,
      subValue: mutedSub(t('monthlyReports.totalPayoutAmount', { amount: formatCurrency(summary.total_payout_amount || 0) })),
    },
    {
      icon: faPercent, iconClassName: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      label: t('monthlyReports.payoutFee'),
      value: <span className="text-orange-600 dark:text-orange-400">฿{fmtMoney(summary.total_payout_fee || 0)}</span>,
      subValue: mutedSub(t('monthlyReports.totalPayoutFeeLabel')),
    },
    {
      icon: faDroplet, iconClassName: 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400',
      label: 'ค่าธรรมเนียม P2P',
      value: <span className="text-sky-600 dark:text-sky-400">฿{fmtMoney(summary.total_p2p_fee || 0)}</span>,
      subValue: mutedSub('หักจากกระเป๋า fee P2P'),
    },
    {
      icon: faCoins, iconClassName: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      label: t('monthlyReports.totalAllFee'),
      value: <span className="text-purple-600 dark:text-purple-400">฿{fmtMoney((summary.total_fee || 0) + (summary.total_payout_fee || 0) + (summary.total_p2p_fee || 0))}</span>,
      subValue: mutedSub(`${t('monthlyReports.payinAndPayout', { payin: fmtMoney(summary.total_fee || 0), payout: fmtMoney(summary.total_payout_fee || 0) })} + P2P ฿${fmtMoney(summary.total_p2p_fee || 0)}`),
    },
  ];

  return (
    <div className="w-full">
      <UiPageHeader
        icon={faCalendarAlt}
        title={t('monthlyReports.title')}
        subtitle={t('monthlyReports.subtitle', { month: months[month - 1], year })}
      />

      {/* Month/Year selector + Export */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme p-4 mb-6 flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <UiSelect
            wrapperClassName="w-fit min-w-[140px]"
            label={t('common.month', 'เดือน')}
            icon={faCalendarAlt}
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value))}
          >
            {months.map((name, i) => (
              <option key={i} value={i + 1}>{name}</option>
            ))}
          </UiSelect>
          <UiSelect
            wrapperClassName="w-fit min-w-[100px]"
            label={t('common.year', 'ปี')}
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </UiSelect>
        </div>

        <UiButton
          variant="secondary"
          icon={faFileExport}
          onClick={handleExportCSV}
          disabled={loading || !(report.days && report.days.length)}
        >
          {t('monthlyReports.exportCsv')}
        </UiButton>
      </div>

      {/* Summary Cards */}
      <UiStatsGrid cards={summaryCards} loading={loading} cols={4} />

      {/* Payout Fee Summary */}
      {(summary.total_payouts > 0 || summary.total_payout_fee > 0 || (summary.total_p2p_fee || 0) > 0) && (
        <UiStatsGrid cards={payoutCards} loading={loading} cols={4} />
      )}

      <P2pReportPanel summary={p2p} showMerchantCount={false} />

      {/* Daily Report Table */}
      <DataTable
        data={report.days || []}
        columns={columns}
        loading={loading}
        emptyMessage={t('monthlyReports.noDataThisMonth')}
        emptyIcon={<FontAwesomeIcon icon={faChartPie} className="text-5xl text-gray-200 dark:text-slate-700" />}
        pagination={{ pageSize: 31, showPageSizeSelector: false }}
        headerContent={
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-lg" style={{ color: 'var(--theme-primary)' }} />
            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100">
              {t('monthlyReports.dailyReport', { month: months[month - 1], year })}
            </h2>
          </div>
        }
        hoverable
      />
    </div>
  );
};

export default MerchantMonthlyReportsPage;
